// VIS-239: a map whose graphics device dies. On an iPhone 12 the GPU process was killed (memory) after the map had
// opened: every frame then threw ("GPUDevice.createCommandEncoder: Unable to make command encoder") before its draw,
// the 3D picture stood frozen under a working interface, and Sentry got thousands of the same error. device.lost never
// fired. What happens now (main.js wires it):
//  1. The frame is guarded (main.js frameLoop). A graphics error stops the loop at once and is reported once; any other
//     error is reported once and the loop goes on.
//  2. A watchdog: once the map is ready, a loop that keeps running while the map should draw (shown, awake, no room
//     tour) but draws nothing for 3 s counts as a lost device too (another error every frame, or a silent loss). Like
//     the frames, it reads performance.now(): a held QA loop (docs/qa/lib/held.mjs) or one stopped by a harness is
//     never judged. three's own device-lost callback (main.js onDeviceLost) takes the same way out.
//  3. The way out (lost()): the device's failure is remembered (localStorage LOST_KEY { lost, at, release, set }), and
//     the page reloads once (sessionStorage RELOAD_KEY: never a loop). That page, and every later visit from this
//     device, opens on the rescue set (main.js RESCUE: the phone set plus 1x drawing density, a 1024 far shadow map,
//     no ambient occlusion, bloom or soft light). A rescue set that fails as well, a second loss in the same tab, or
//     no storage to guard the reload with: the loader's "couldn't load / Try again" box instead of a frozen map.
//  4. VIS-255: the ladder ends there. Phones showed the map, went blank, showed it again, went blank: the rescue set
//     failed too, and every Try again and every new visit started the 3D again only to lose it again. Now the stored
//     failure counts the losses (`n`); once the rescue set has failed as well (n >= 2), the next starts within STOP_MS
//     show the "couldn't load" box at once, without starting the 3D (boot.js stopped()). Only the guest's own Try again
//     on that box allows one more start (on the rescue set; TRY_KEY, this tab, used once). After STOP_MS the device tries
//     the rescue set once again by itself. So a device draws at most: its own set, the rescue set, then the box.
//  5. VIS-267 (phones that never opened): one automatic reload at most, and only onto the safer path. Chromium blocks
//     3D for a page after GPU-process crashes and a reload by script stays blocked, so the goal is no second WebGPU
//     crash at all: the stored failure also remembers the backend that lost (`backend`, and `webgpu`: a loss on WebGPU
//     ever, kept). After a WebGPU loss on Android the next start takes the rescue set on the WebGL2 backend
//     (startPath); iPhones and computers keep WebGPU on the rescue set (every loss in the field was a phone's; WebGL2
//     on a computer draws another picture). A second loss shows the box (as before). A start that died without a word
//     (Safari often never fires device.lost; the tab is killed) counts as a loss too: main.js keeps the start under
//     way in localStorage (START_KEY { id, step, t, set, backend }, createStartRecord) until the map has run 10 s or
//     the page is hidden or left; boot.js finds an unfinished one (takeUnfinished), counts it (countLoss) and takes the
//     safer path. A start another tab is still running is not unfinished: each page holds a Web Lock named after its
//     record while it starts (the browser frees it when the page dies), and boot.js passes the held names
//     (liveStarts). The first sign of a dead device stops the frame loop at once (createFrameRunner, isLossError), and
//     a page takes one loss (firstLoss).
//  6. VIS-267 (the owner's phones, 2026-10-25): the memory belongs to the release it was made on. An iPhone 16 Pro and
//     a 13 Pro lost their graphics for real on builds of 24 Oct that used more graphics memory, and the lighter build
//     of the next day still opened on the rescue set (16 Pro) or on the box (13 Pro, n >= 2): a memory of 30 days,
//     tied to nothing. Now a loss remembers its release (`release`) and how many releases this device lost on (`rel`):
//     one loss, on an older release, is not read (readLost: the new release gets one fresh try on the device's own
//     set; the box does not outlive a release either), and the memory keeps 7 days, not 30. A device that lost on two
//     releases (rel >= 2: the iPhone 12 that dies on every build) keeps its memory across releases, for a day after its
//     last loss (RETRY_MS; the owner, 2026-11-03: Safari 27.2 lost on every opening until PR #684 fixed the cause, and
//     his phone still opened on the lighter set until he deleted the site data: a fixed cause must not keep phones
//     there for a week). Past the day a newer release gets the same fresh try; a loss on it writes the memory again
//     (7 days on that release, a day on the next, `rel` goes on) and the ladder runs as ever. A map that ran 10 s
//     past Ready without a loss (createStartRecord end) heals: on the rescue set the box goes (n = 1, the rescue set
//     stays), on the device's own set the memory goes (it drew its own set: an old release's, or QA ?phoneset=1).
//  7. A computer is not a phone (the owner, 2026-10-29: "a lighter version on a MacBook that costs 5,500 euros?"). A
//     MacBook Pro on Safari 26 lost its graphics twice in 15 minutes on the full set ("GPUCommandEncoder.finish: Unable
//     to finish": Safari's graphics process went away, MAPMYSTAY-VIEWER-G / -2A) and the reload put it on the rescue
//     set, made for phones that run out of memory. A computer's loss is its browser's hiccup, not too little memory:
//     its first loss reloads onto its own set again (a fresh graphics process); a second loss (in the tab, or in the
//     memory: `k` counts the losses on the device's own set; two releases lost on count as two) takes the rescue set,
//     then the box. So a computer draws at most: its own set twice, the rescue set, then the box (computerDevice:
//     errors.js deviceFacts' `computer`). The reload mark says whether the next start is the lighter one (`lighter`),
//     so the website's cover says "lighter" only when it is.
//  8. An iPhone gets one more rung (2026-10-31, Sentry MAPMYSTAY-VIEWER-Y: one iPhone on iOS 27 pressed Try again
//     seven times in five minutes and saw the box every time). On iOS 26 / 27 only the WebGPU backend loses its
//     device (17 losses in 14 days, all WebGPU; the older iPhones on the WebGL2 backend lost none, and the phones
//     without WebGPU already open on the rescue set on that backend: openingSet). So once the rescue set has
//     lost on WebGPU, the next start draws the rescue set on the WebGL2 backend (appleMobile, startPath, lostAction:
//     one more reload, in this tab at most two), and only a loss there shows the box (stopped). An iPhone draws at
//     most: its own set, the rescue set, the rescue set on WebGL2, then the box. Android keeps its own ladder (point 5:
//     Chromium blocks 3D after a GPU crash), computers theirs (point 7).
// Every storage access is wrapped: a private window, blocked site data or a full disk throws, and then the page
// simply knows less (no reload without its guard).

export const LOST_KEY = 'mapmystay-gpu-lost'; // localStorage: this device lost its graphics on the map before
export const RELOAD_KEY = 'mapmystay-gpu-reload'; // sessionStorage: this tab already reloaded once for it
const TRY_KEY = 'mapmystay-gpu-try'; // sessionStorage: the guest pressed Try again on a stopped device: one more start
export const KEEP_MS = 7 * 864e5; // a remembered failure counts for 7 days (was 30 until 2026-10-25), then the device tries its own set again
export const RETRY_MS = 864e5; // 2026-11-03: a memory of an older release counts for a day after its loss, then that release gets a fresh try
export const READY_STEP = 'Ready'; // the start record's last step (main.js progress(1, 'Ready')): a start that got here and ended by itself ran well
const STOP_MS = 864e5; // VIS-255: after the rescue set failed too, no new 3D start by itself for a day
export const START_KEY = 'mapmystay-start'; // VIS-267 localStorage: the start under way (main.js createStartRecord), gone once the map ran 10 s

const get = (store, key) => { try { return store()?.getItem(key) ?? null; } catch { return null; } }; // errors:ignore: storage blocked = nothing stored
const put = (store, key, value) => { try { store().setItem(key, value); return true; } catch { return false; } }; // errors:ignore: storage blocked or full = not stored (the caller decides)
const drop = (store, key) => { try { store()?.removeItem(key); } catch { /* errors:ignore: storage blocked */ } };

const count = (v, k) => Math.max(1, Math.floor(Number(v[k])) || 1); // a stored count, at least 1 (a record from before the field has none)
/** The stored failure within KEEP_MS, whatever release it was made on (readLost applies the release rule). */
function readRaw(local, now) {
  try {
    const v = JSON.parse(get(local, LOST_KEY) || 'null');
    if (!v || v.lost !== true || !(now - Date.parse(v.at) < KEEP_MS)) return null; // a 7-day memory: a computer's one driver hiccup is not for good (VIS-239 records have no n: one loss)
    return { ...v, webgpu: v.webgpu !== false, n: count(v, 'n'), rel: count(v, 'rel'), k: count(v, 'k') };
  } catch { return null; } // errors:ignore: an unreadable flag counts as none
}

/**
 * The failure this device remembers, or null. `n`: losses so far (1 = its own set, 2 or more = the rescue set too).
 * VIS-267: `backend` the last loss drew on; `webgpu` = a loss on WebGPU, ever (a record from before VIS-267 has
 * neither: every loss then was on WebGPU). `rel`: how many releases this device lost on. With `release` (the page's
 * own, errors.js RELEASE) a record that is not this release's is null: that release gets a fresh try (the loss may have
 * been the build's, not the device's), unless the device lost on two releases and the loss is under RETRY_MS old.
 * Without `release` (QA, tests) every record counts. `k`: losses on the device's own set (a computer's second takes the
 * rescue set).
 * @param {() => Storage} local  @param {number} [now]  @param {string} [release]
 * @returns {{ lost: true, at: string, release: string, set: string, backend?: string, webgpu: boolean, n: number, rel: number, k: number } | null}
 */
export function readLost(local, now = Date.now(), release) {
  const v = readRaw(local, now);
  if (!v || release == null || v.release === String(release).slice(0, 40)) return v;
  return v.rel >= 2 && now - Date.parse(v.at) < RETRY_MS ? v : null; // an older release's memory: this release gets one fresh try on the device's own set
}

/**
 * VIS-255: does this start show the "couldn't load" box at once, without starting the 3D? Yes when the rescue set
 * failed on this device too, less than STOP_MS ago, unless the guest pressed Try again (TRY_KEY: used up here, so the
 * one start it allows is the only one). ?phoneset= (QA) never stops. VIS-267: nor a memory of another release (readLost).
 * Point 8: an iPhone (`apple`, appleMobile) whose rescue set lost on WebGPU is not stopped: its next start draws the
 * rescue set on the WebGL2 backend (startPath); only a loss on that backend too stops it.
 * @param {{ param?: string | null, local: () => Storage, session: () => Storage, now?: number, release?: string, apple?: boolean }} o
 */
export function stopped({ param, local, session, now = Date.now(), release, apple = false }) {
  if (param === 'rescue' || param === '0' || param === '1') return false;
  const v = readLost(local, now, release);
  if (!v || v.n < 2 || now - Date.parse(v.at) >= STOP_MS) return false;
  if (apple && v.set === 'rescue' && v.backend === 'webgpu') return false; // point 8: the rescue set lost on WebGPU: one more rung (WebGL2) before the box
  if (get(session, TRY_KEY) !== null) { drop(session, TRY_KEY); return false; }
  return true;
}

/** VIS-255: the guest's Try again on the "couldn't load" box of a lost device: one more start in this tab. */
export function allowTry({ session, now = Date.now() }) { put(session, TRY_KEY, String(now)); }

/**
 * VIS-267: does a WebGL2 context really come? A throwaway canvas's context, released at once (WEBGL_lose_context), so
 * it holds nothing. Chromium answers null once it blocked 3D for the page (GPU-process crashes).
 * @param {() => { getContext: (kind: string) => any }} makeCanvas
 */
export function hasWebGL2(makeCanvas) {
  try {
    const gl = makeCanvas().getContext('webgl2');
    if (!gl) return false;
    gl.getExtension?.('WEBGL_lose_context')?.loseContext();
    return true;
  } catch { return false; } // errors:ignore: a throwing getContext = no WebGL2
}

/**
 * VIS-267: the WebGL2 renderer string ('' = no context or no name), from a throwaway context as hasWebGL2 makes one, for
 * openingSet (Samsung's Xclipse GPU: Chromium's WebGPU blocklist gives it no adapter).
 * @param {() => { getContext: (kind: string) => any }} makeCanvas
 */
export function glName(makeCanvas) {
  let gl = null;
  try {
    gl = makeCanvas().getContext('webgl2');
    const dbg = gl?.getExtension('WEBGL_debug_renderer_info');
    return gl ? String(gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || '') : '';
  } catch { return ''; } finally { try { gl?.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* errors:ignore: it holds nothing */ } } // errors:ignore: no name = not Samsung's GPU (openingSet: the rescue set)
}

/**
 * Does this page open on the rescue set? Asked for (?phoneset=rescue), or this device (or this tab, when only the
 * session could keep it) lost its graphics before. An explicit ?phoneset=0 / 1 wins, so side-by-sides stay exact.
 * VIS-267: `release` (the page's own) = a memory of one older release does not count (readLost). `computer`
 * (computerDevice): only a second loss counts (point 7 at the top).
 * @param {{ param?: string | null, local: () => Storage, session: () => Storage, release?: string, computer?: boolean, now?: number }} o
 */
export function rescueAsked({ param, local, session, release, computer = false, now = Date.now() }) {
  if (param === 'rescue') return true;
  if (param === '0' || param === '1') return false;
  const v = readLost(local, now, release), done = reloads(session);
  if (computer) return (!!v && (v.set === 'rescue' || v.k >= 2 || v.rel >= 2)) || done >= 2;
  return !!v || done > 0;
}

/** The reloads this tab made for a lost device (RELOAD_KEY `n`; a mark from before it has none: one). */
function reloads(session) {
  const raw = get(session, RELOAD_KEY);
  if (raw === null) return 0;
  try { return count(JSON.parse(raw) || {}, 'n'); } catch { return 1; } // errors:ignore: a VIS-239 mark (a bare time): one reload
}

/**
 * Point 7: a computer (a desktop or laptop browser; lobby screens too), as errors.js deviceFacts tells it: not an
 * iPhone, iPad (also with a Mac's user agent and a touch screen) or Android device.
 * @param {string} [ua]  @param {number} [touch] navigator.maxTouchPoints
 */
export const computerDevice = (ua = '', touch = 0) => !!ua && !/iPhone|iPad|iPod|Android/.test(ua) && !(/Macintosh/.test(ua) && touch > 1);

/**
 * Point 8: an iPhone or iPad (also one with a Mac's user agent and a touch screen), as errors.js platformOf tells it
 * ('ios'). Never a computer (computerDevice) or an Android device (androidBrowser) at the same time.
 * @param {string} [ua]  @param {number} [touch] navigator.maxTouchPoints
 */
export const appleMobile = (ua = '', touch = 0) => /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touch > 1);

/**
 * Point 8: does this stored failure put an iPhone's next start on the WebGL2 backend? Yes once its rescue set has lost
 * (on an iPhone with WebGPU the record says `rescue` only after the rescue set itself lost there), and from then on: a
 * rescue set that lost on WebGL2 too is stopped() for a day, one that ran well there heals to n = 1 and keeps drawing
 * on WebGL2. A record of a device that never had WebGPU (`webgpu` false: the older iPhones) answers false; they draw
 * on WebGL2 anyway.
 * @param {{ set: string, webgpu: boolean }} v  readLost's record
 */
const webgl2Rung = (v) => v.set === 'rescue' && v.webgpu;

/**
 * Remember a loss on this device (the set and the backend it drew with). VIS-267: also a start that died without a
 * word (boot.js, takeUnfinished). The start record goes: this start's end is known, the next start does not count it.
 * `rel` counts the releases this device lost on (a memory of an older release is read here, so the count goes on).
 * @param {{ set: 'full' | 'phone' | 'rescue', backend?: string, release?: string, local: () => Storage, now?: number }} o
 */
export function countLoss({ set, backend = '', release = 'dev', local, now = Date.now() }) {
  const rel0 = String(release).slice(0, 40), raw = readRaw(local, now), was = readLost(local, now, rel0);
  const rel = !raw ? 1 : raw.release === rel0 ? raw.rel : raw.rel + 1; // VIS-267: a second release lost on: the device's, not a build's
  const n = set === 'rescue' ? (was?.n ?? 1) + 1 : 1; // VIS-255: a loss on the rescue set counts on top (n >= 2: stopped()); an older release's count does not carry
  const webgpu = backend === 'webgpu' || !!raw?.webgpu; // VIS-267: once WebGPU died here, the safer path stays (startPath)
  const k = set === 'rescue' ? (was?.k ?? 1) : (was?.k ?? 0) + 1; // point 7: the losses on the device's own set (a computer's second takes the rescue set)
  put(local, LOST_KEY, JSON.stringify({ lost: true, at: new Date(now).toISOString(), release: rel0, set, backend: String(backend).slice(0, 20), webgpu, n, rel, k }));
  own?.lost(); // this page's own start record (another tab's start is left alone)
}

/**
 * VIS-267: the map ran well (10 s past Ready, no loss on this page: createStartRecord end): on the rescue set the box
 * goes (n = 1; the rescue set stays until its memory is over), on the device's own set the memory goes (the device
 * drew its own set: an older release's memory, or QA's ?phoneset=0 / 1).
 * @param {{ set: string, local: () => Storage, now?: number }} o
 */
export function ranWell({ set, local, now = Date.now() }) {
  if (pageLost) return;
  const raw = readRaw(local, now);
  if (!raw) return;
  if (set !== 'rescue') drop(local, LOST_KEY);
  else if (raw.n > 1) put(local, LOST_KEY, JSON.stringify({ ...raw, n: 1 }));
}

/**
 * The way out after a lost device: remember it, then 'reload' (once a tab, onto the safer path: startPath; a computer
 * twice, its own set then the rescue set: point 7; an iPhone twice, the rescue set then the rescue set on WebGL2:
 * point 8, `apple`) or 'fail' (show the error box).
 * @param {{ set: 'full' | 'phone' | 'rescue', backend?: string, release?: string, local: () => Storage, session: () => Storage, now?: number, computer?: boolean, apple?: boolean }} o
 * @returns {'reload' | 'fail'}
 */
export function lostAction({ set, backend = '', release = 'dev', local, session, now = Date.now(), computer = false, apple = false }) {
  countLoss({ set, backend, release, local, now });
  const done = reloads(session);
  const more = apple && set === 'rescue' && backend === 'webgpu'; // point 8: the iPhone's rescue set lost on WebGPU: once more, on WebGL2
  if ((set === 'rescue' && !more) || done >= (computer || more ? 2 : 1)) return 'fail'; // the lightest set failed too, or this tab reloaded already
  // `lighter`: the next start is on the rescue set (rescueAsked with this mark), for the website's cover
  const lighter = !computer || done + 1 >= 2 || rescueAsked({ param: null, local, session: () => null, release, computer, now });
  return put(session, RELOAD_KEY, JSON.stringify({ at: now, webgpu: backend === 'webgpu', n: done + 1, lighter })) ? 'reload' : 'fail'; // no guard against a loop = no reload
}

/**
 * VIS-267: an Android browser: after a WebGPU loss its next start moves to the WebGL2 backend (every lost device in
 * the field was a phone's). iPhones and computers keep WebGPU on the rescue set: a computer's driver hiccup is no
 * phone's memory, and WebGL2 on a computer draws another picture (performance.md).
 * @param {string} [ua]
 */
export const androidBrowser = (ua = '') => /Android/.test(ua);

/**
 * VIS-267: the path of this start. `rescue` = rescueAsked; `webgl2` = after a loss on WebGPU, the WebGL2 backend
 * (Android: after any WebGPU loss; an iPhone, point 8 `apple`: after the rescue set lost on WebGPU, webgl2Rung; not
 * with ?phoneset=, so side-by-sides stay exact). `release`: the page's own (errors.js RELEASE). `computer`: point 7
 * (computerDevice).
 * @param {{ param?: string | null, local: () => Storage, session: () => Storage, android?: boolean, release?: string, computer?: boolean, apple?: boolean }} o
 * @returns {{ rescue: boolean, webgl2: boolean }}
 */
export function startPath({ param, local, session, android = false, release, computer = false, apple = false }) {
  const rescue = rescueAsked({ param, local, session, release, computer });
  if (!rescue || param || !(android || apple)) return { rescue, webgl2: false };
  const v = readLost(local, Date.now(), release);
  if (apple) { // point 8: the record, or (only the session left) this tab's reload mark: its second reload after a WebGPU loss
    if (v) return { rescue, webgl2: webgl2Rung(v) };
    try { const m = JSON.parse(get(session, RELOAD_KEY) || 'null'); return { rescue, webgl2: !!m && typeof m === 'object' && count(m, 'n') >= 2 && m.webgpu !== false }; } catch { return { rescue, webgl2: false }; } // errors:ignore: a VIS-239 guard (a bare time): one reload, the rescue set on WebGPU
  }
  if (v) return { rescue, webgl2: v.webgpu };
  try { return { rescue, webgl2: JSON.parse(get(session, RELOAD_KEY) || 'null')?.webgpu !== false }; } catch { return { rescue, webgl2: true }; } // errors:ignore: a VIS-239 guard (a bare time): that loss was on WebGPU
}

/**
 * VIS-267: the set this start opens on and its backend, from the facts main.js asks once. `gl`: the WebGL2 backend (the
 * safer path after a loss, a weak adapter: `weak`, the backend's reason, or none: also when forced or without WebGPU).
 * `mobile` gives the phone set (not a lobby screen; `?phoneset=0 | 1` exact). A phone on WebGL2 from the start opens on
 * the rescue set (the red slivers: on that backend a memory shortage is silent; half the graphics memory), except an
 * Android browser whose WebGPU (`gpu`: asked for, not forced) gave no adapter, with `memory` (GB, the browser's) >= 8,
 * on Samsung's Xclipse GPU (`renderer`: glName): Chrome's WebGPU blocklist gives it none, and the phone set draws right
 * there on WebGL2 (other GPUs it blocks, for their drivers' faults, and in-app browsers keep the rescue set); a lost
 * context takes the loss ladder. Never a computer without touch (point 7: "a lighter version" is for phones; `touch`:
 * a tablet or phone whose browser reads as a computer, "Desktop site", keeps it).
 * @param {{ path: { rescue: boolean, webgl2: boolean }, param?: string | null, mobile: boolean, lobby?: boolean, gpu?: boolean, adapter?: { features: { has(f: string): boolean }, limits: { maxTextureDimension2D: number } } | null, android?: boolean, memory?: number, renderer?: string, computer?: boolean, touch?: boolean }} o
 * @returns {{ set: 'full' | 'phone' | 'rescue', gl: boolean, weak: '' | 'compat-only' | 'small-textures' }}
 */
export function openingSet({ path, param, mobile, lobby, gpu, adapter, android, memory, renderer = '', computer, touch }) {
  const weak = !adapter ? '' : !adapter.features.has('core-features-and-limits') ? 'compat-only' : adapter.limits.maxTextureDimension2D < 4096 ? 'small-textures' : '';
  const gl = path.webgl2 || !!weak || !adapter, phone = param === '1' || (param !== '0' && mobile && !lobby);
  const light = gl && phone && param == null && !(computer && !touch) && !(android && gpu && !adapter && memory >= 8 && /xclipse|samsung/i.test(renderer));
  return { set: path.rescue || light ? 'rescue' : phone ? 'phone' : 'full', gl, weak };
}

/**
 * VIS-267 (the red slivers): the WebGL2 backend's own error flags, drained now. three never asks for them, so a buffer
 * or picture it could not allocate was drawn anyway. 'out-of-memory' | 'context-lost' | null (any other flag is dropped).
 * @param {{ backend?: { isWebGLBackend?: boolean, gl?: WebGL2RenderingContext } } | null} renderer
 */
export function glError(renderer) {
  const b = renderer?.backend, gl = b?.isWebGLBackend ? b.gl : null;
  if (!gl || typeof gl.getError !== 'function') return null;
  let why = null;
  for (let i = 0; i < 8; i++) { // one flag a call; the context keeps up to one per kind
    const e = gl.getError();
    if (!e) break;
    if (e === gl.CONTEXT_LOST_WEBGL) why = 'context-lost';
    else if (e === gl.OUT_OF_MEMORY && why !== 'context-lost') why = 'out-of-memory';
  }
  return why;
}

/** QA (resortMap.phoneSet.forget()): this device opens on its own set again. */
export function forgetLost({ local, session }) { drop(local, LOST_KEY); drop(session, RELOAD_KEY); drop(session, TRY_KEY); drop(local, START_KEY); }

let pageLost = false; // VIS-267: this page took its loss (main.js's guard, or boot.js for a build that died of it)
/** VIS-267: one loss a page: true for the first caller only (a lost device also rejects the build, and fires device.lost late). */
export function firstLoss() { if (pageLost) return false; pageLost = true; return true; }

let own = null; // VIS-267: this page's start record (createStartRecord), for countLoss and boot.js (ownStart, endStart)
const lockName = (id) => `${START_KEY}:${id}`;
const storedId = (local) => { try { return JSON.parse(get(local, START_KEY) || 'null')?.id ?? null; } catch { return null; } }; // errors:ignore: an unreadable record is no one's

/**
 * VIS-267: the start under way, in localStorage until the map has run 10 s. main.js calls step() at each loading step
 * once the renderer is up, away(true) when the page is hidden or left (a guest who leaves is no loss; shown again:
 * written again), end() at ready + 10 s. Nothing is written once this page took a loss (firstLoss). The record
 * carries this page's id, and the page holds a Web Lock of that name while it starts (the record is written only
 * once the lock is held, so another tab never sees it without the lock): a tab that starts meanwhile does not count
 * a live start (takeUnfinished `live`); the browser frees the lock when the page dies. Only the page's own record is
 * ever dropped. No Web Locks: written at once, as before. An end() at READY_STEP on a page that took no loss (main.js's
 * 10 s past Ready) is a map that ran well (ranWell); countLoss and endStart end the record before that step, or after
 * a loss.
 * @param {{ local: () => Storage, set: string, backend: string, now?: () => number, locks?: { request?: Function } | null }} o
 */
export function createStartRecord({ local, set, backend, now = Date.now, locks = globalThis.navigator?.locks }) {
  const id = `${now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  let step = '', ended = false, held = typeof locks?.request !== 'function', release = null, hidden = false;
  // hidden: the page is hidden or left (away(true)); a step reached meanwhile (a hidden page parks at the next frame
  // wait) is not written back, or a tab the browser later closes in the background counts as a crash on the next visit
  // (Sentry MAPMYSTAY-VIEWER-1Q / -1W / -1X, 2026-10-26: a Mac, an Android and an Edge laptop sent to the rescue set)
  const write = () => { if (held && !ended && !pageLost && !hidden) put(local, START_KEY, JSON.stringify({ id, step: step.slice(0, 80), t: now(), set, backend })); };
  const dropOwn = () => { if (storedId(local) === id) drop(local, START_KEY); };
  if (!held) {
    try {
      Promise.resolve(locks.request(lockName(id), () => (ended ? null : new Promise((r) => { release = r; held = true; write(); })))).catch(() => {}); // errors:ignore: no lock = no record (a false failure is worse than a missed one)
    } catch { /* errors:ignore: no lock = no record */ }
  }
  const rec = {
    id, set, backend,
    step(msg) { if (msg && msg !== step) { step = msg; write(); } },
    away(gone) { if (ended) return; hidden = gone; if (gone) dropOwn(); else write(); },
    end() { rec.lost(); if (step === READY_STEP && !pageLost) ranWell({ set, local, now: now() }); }, // VIS-267: ran 10 s past Ready without a loss = ran well
    lost() { ended = true; dropOwn(); release?.(); }, // the start ended in a counted loss (countLoss) or a known failure (endStart): the record goes, nothing heals
  };
  own = rec;
  return rec;
}

/** VIS-267 (boot.js): this page's start record once main.js's renderer is up ({ set, backend }), else null. */
export const ownStart = () => own;
/** VIS-267 (boot.js): this start ended with a known failure: its record goes (no silent loss for the next start). */
export function endStart() { own?.lost(); }

/**
 * VIS-267 (boot.js): the names of the starts other tabs are running now (their Web Locks), or null when the browser
 * cannot say (no Web Locks: every record then counts, as before).
 * @param {{ query?: () => Promise<{ held?: { name?: string }[] }> } | null} [locks]
 * @returns {Promise<Set<string> | null>}
 */
export async function liveStarts(locks = globalThis.navigator?.locks) {
  try {
    if (typeof locks?.query !== 'function') return null;
    const q = await locks.query();
    return new Set((q?.held || []).map((l) => String(l?.name)));
  } catch { return null; } // errors:ignore: no answer = no list
}

/**
 * VIS-267 (boot.js, before anything starts): the previous start that never finished, or null; read once (it goes).
 * Only a start whose renderer was up counts (it has a set and a backend), and only within KEEP_MS. A start another
 * tab still runs (its lock is in `live`) is no failure: it stays.
 * @param {{ local: () => Storage, now?: number, live?: Set<string> | null }} o
 * @returns {{ step: string, t: number, set: 'full' | 'phone' | 'rescue', backend: string } | null}
 */
export function takeUnfinished({ local, now = Date.now(), live = null }) {
  let v;
  try { v = JSON.parse(get(local, START_KEY) || 'null'); } catch { v = null; } // errors:ignore: an unreadable record counts as none
  if (v?.id && live?.has(lockName(v.id))) return null; // another tab's start, under way
  drop(local, START_KEY);
  if (!v || !/^(full|phone|rescue)$/.test(v.set) || typeof v.backend !== 'string' || !v.backend || !(now - Number(v.t) < KEEP_MS)) return null;
  return { step: String(v.step || 'before the first step').slice(0, 80), t: Number(v.t), set: v.set, backend: v.backend.slice(0, 20) };
}

/**
 * An error from the graphics device (as WebGPU and the browsers word it), as against a plain bug in a frame's step.
 * @param {unknown} err
 */
export function isGpuError(err) {
  const e = /** @type {any} */ (err);
  if (isLossError(e)) return true; // VIS-267
  if (e?.name === 'TypeError') return false; // a wrong call (writeTexture "Overload resolution failed") is a bug in a step, not a dead device
  return /^(InvalidStateError|OperationError|GPU\w*Error)$/.test(String(e?.name || '')) || /\bGPU|WebGPU|WebGL|command ?encoder|device (?:was |is )?lost|context (?:was |is )?lost/i.test(String(e?.message ?? e ?? ''));
}

/**
 * VIS-267: the first sign of a dead device, never a passing error: a command encoder that cannot be made, an
 * InvalidStateError (a destroyed device), or a RangeError while a buffer is mapped for an upload (getMappedRange,
 * three's createAttribute: out of graphics memory; an iPhone failed so 10 s before its loss).
 * @param {unknown} err
 */
export function isLossError(err) {
  const e = /** @type {any} */ (err), name = String(e?.name || ''), msg = String(e?.message ?? e ?? '');
  if (name === 'InvalidStateError' || /command ?encoder/i.test(msg)) return true;
  return name === 'RangeError' && /getMappedRange|createAttribute/.test(`${msg}\n${e?.stack || ''}`);
}

/**
 * VIS-239 / VIS-267: the guarded frame (main.js frameLoop). The first sign of a dead device (isLossError) fails the
 * guard at once and the loop stops; another graphics error 30 frames in a row does the same (one can be a passing
 * validation error); any other error is reported once per wording (at most 8) and the loop goes on.
 * @param {{ step: (now: number) => void, guard: { failed: boolean, beat: () => void, fail: (err: unknown, why: string) => boolean }, report: (err: unknown) => void, stop: () => void, held?: () => boolean }} o
 */
export function createFrameRunner({ step, guard, report, stop, held = () => false }) {
  const seen = new Set();
  let gpuRun = 0; // frames in a row that ended in a graphics error
  return (now) => {
    if (guard.failed) return stop(); // (a device lost during the build: the loop never starts)
    if (held()) return;
    guard.beat();
    try { step(now); gpuRun = 0; } catch (err) {
      if (isLossError(err)) { guard.fail(err, 'frame'); return stop(); }
      if (isGpuError(err)) { if (++gpuRun >= 30) guard.fail(err, 'frame'); return; }
      const k = String(/** @type {any} */ (err)?.message || err).slice(0, 120);
      if (!seen.has(k) && seen.size < 8) { seen.add(k); report(err); }
    }
  };
}

/**
 * The frame guard and its watchdog. main.js calls beat() at every loop callback, drawn() after a picture was drawn
 * without a throw, arm() once the map is ready and check(shouldDraw) about once a second; fail() is the one way out
 * (onLost runs once).
 * @param {{ onLost: (err: unknown, why: string) => void, stallMs?: number, now?: () => number }} o
 */
export function createGuard({ onLost, stallMs = 3000, now = () => performance.now() }) {
  let armed = false, done = false, beat = -Infinity, good = -Infinity, last = -Infinity;
  const api = {
    arm() { armed = true; good = last = now(); },
    beat() { beat = now(); },
    drawn() { good = now(); },
    /** shouldDraw: the map should be drawing now (ready, shown, awake). Only a loop that ran in the last second is
     *  judged (a loop a harness stopped, or a long task that held the page, starts the 3 s again), and only when this
     *  check's own last run was under 2.5 s ago: a computer that slept with the map open (Windows' clock counts the
     *  sleep; an Edge laptop on Intel Xe-LPG was sent to the rescue set for "no picture drawn for 1317 s" and then
     *  3514 s, Sentry MAPMYSTAY-VIEWER-1K, 2026-10-26) starts the 3 s again; a device that really died is still
     *  caught 3 s after waking */
    check(shouldDraw) {
      if (done || !armed) return;
      const t = now(), gap = t - last;
      last = t;
      if (!shouldDraw || t - beat > 1000 || gap > 2500) { good = t; return; }
      if (t - good > stallMs) api.fail(new Error(`map: no picture drawn for ${Math.round((t - good) / 1000)} s`), 'stalled');
    },
    fail(err, why) { if (done) return false; done = true; onLost(err, why); return true; },
    get failed() { return done; },
  };
  return api;
}
